import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import TermsContent from '../components/Legal/TermsContent';
import { JsonLd } from '../components/Common/JsonLd';

/**
 * Standalone /terms permalink.
 *
 * Same rationale as /privacy — Google's OAuth verification form, the
 * Apple App Store submission flow, and most enterprise procurement
 * checklists all expect a dedicated, scrapeable URL for the Terms
 * of Service. We share the body component (TermsContent) with the
 * /legal hub so updates only happen in one place.
 */
export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Helmet>
        <title>Terms of Service · HitzLink</title>
        <meta
          name="description"
          content="HitzLink terms of service — acceptable use, plans and pricing, third-party integrations (Google Tag Manager, Meta CAPI), and account termination."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://hitzlink.com/terms" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Terms of Service · HitzLink" />
        <meta property="og:url" content="https://hitzlink.com/terms" />
      </Helmet>

      <JsonLd
        id="terms-webpage"
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': 'https://hitzlink.com/terms',
          url: 'https://hitzlink.com/terms',
          name: 'Terms of Service',
          description:
            'HitzLink terms of service — acceptable use, plans and pricing, third-party integrations, and account termination.',
          inLanguage: 'th',
          isPartOf: { '@id': 'https://hitzlink.com#website' },
          publisher: { '@id': 'https://hitzlink.com#organization' },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hitzlink.com/' },
              { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: 'https://hitzlink.com/terms' },
            ],
          },
        }}
      />

      <div className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-gray-900/30 backdrop-blur-sm rounded-lg border border-gray-800 p-6 sm:p-8">
          <TermsContent />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
          <button
            onClick={() => navigate('/privacy')}
            className="hover:text-white transition-colors"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate('/legal')}
            className="hover:text-white transition-colors"
          >
            All legal documents
          </button>
          <a
            href="mailto:support@hitzlink.com"
            className="hover:text-white transition-colors"
          >
            Contact support
          </a>
        </div>
      </main>

      <footer className="border-t border-gray-800 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center text-sm text-gray-400">
          <div>© {new Date().getFullYear()} Hitz Tech Solutions Co., Ltd. All rights reserved.</div>
          <div className="text-xs text-gray-500 mt-1">บริษัท ฮิตซ์เทค โซลูชั่นส์ จำกัด</div>
        </div>
      </footer>
    </div>
  );
}
